import { cva } from 'class-variance-authority';
import { cn, Flex } from '@bridebook/aura';
import { PropsWithChildren } from 'react';
import {
  LabelFieldContext,
  LabelFieldContextProps,
  useLabelFieldContext,
} from './label-field-context';

type LabelFieldProps = LabelFieldContextProps & {
  className?: string;
};

export const LabelField = ({
  disabled = false,
  size,
  children,
  fieldId,
  supportingId,
  errorId,
  className,
}: PropsWithChildren<LabelFieldProps>) => (
  <LabelFieldContext.Provider
    value={{
      size,
      disabled,
      fieldId,
      supportingId,
      errorId,
    }}>
    <Flex className={cn('flex-col gap-1', className)}>{children}</Flex>
  </LabelFieldContext.Provider>
);

// Title
const titleVariants = cva(
  'h-full cursor-pointer font-normal text-space select-none group-data-[disabled]:cursor-not-allowed',
  {
    variants: {
      size: {
        standard: 'text-md leading-[150%]',
        sm: 'text-sm leading-[20px]',
      },
      disabled: {
        true: 'cursor-not-allowed',
        false: '',
      },
    },
  },
);

type LabelFieldMainProps = {
  className?: string;
};
export const LabelFieldMain = ({ children, className }: PropsWithChildren<LabelFieldMainProps>) => {
  const { size, fieldId, disabled } = useLabelFieldContext();
  return (
    <label htmlFor={fieldId} className={cn(titleVariants({ size, disabled, className }))}>
      {children}
    </label>
  );
};

// Description
const descriptionVariants = cva('text-space-60', {
  variants: {
    size: {
      standard: 'text-sm leading-[150%]',
      sm: 'text-xs leading-[150%]',
    },
    error: {
      true: 'text-errors',
      false: '',
    },
  },
});

type LabelFieldSupportingProps = {
  className?: string;
};
export const LabelFieldSupporting = ({
  children,
  className,
}: PropsWithChildren<LabelFieldSupportingProps>) => {
  const { size, supportingId } = useLabelFieldContext();
  return (
    <span id={supportingId} className={cn(descriptionVariants({ size, className }), '')}>
      {children}
    </span>
  );
};

// Error
type LabelFieldErrorProps = {
  className?: string;
};
export const LabelFieldError = ({
  children,
  className,
}: PropsWithChildren<LabelFieldErrorProps>) => {
  const { size, errorId } = useLabelFieldContext();
  return (
    <span id={errorId} className={cn(descriptionVariants({ size, error: true, className }))}>
      {children}
    </span>
  );
};
